/**
 * The planner's "now" line (spec 2026-10-07): where now falls in today's list, between the last todo
 * whose time has passed and the next one. A 0-height wrapper between two rows, with a hairline and a
 * glowing dot drawn OVER the boundary, so no row moves — the home card multiplies a measured row
 * height for its cap. Decorative: screen readers skip it (the day heading says "next at …" instead),
 * and it never takes a press or a hover from the rows under it. zIndex 1 keeps it over the next row's
 * web hover plate.
 */
import React from 'react';
import { View, StyleSheet, type LayoutChangeEvent } from 'react-native';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';

/** The rows' divider weight: between rows the line covers the divider there. */
const BAR = 1;
const DOT = 8;
/** The soft glow around the dot. Its left edge is the list's, so the dot's centre is at GLOW / 2. */
const GLOW = 14;

export interface NowLineProps {
  /** At the list's top or bottom edge: move inside it by the glow's radius so the edge can't clip it. */
  edge?: 'top' | 'bottom';
  /** The wrapper's layout: its y is the line's offset in the list (the home card scrolls to it). */
  onLayout?: (e: LayoutChangeEvent) => void;
}

export const NowLine: React.FC<NowLineProps> = ({ edge, onLayout }) => {
  const styles = useThemedStyles(({ semantic, mode }) =>
    StyleSheet.create({
      wrap: {
        height: 0,
        zIndex: 1,
        pointerEvents: 'none',
      },
      // From the dot's centre, so none of the line shows through the glow to the dot's left.
      bar: {
        position: 'absolute',
        left: GLOW / 2,
        right: 0,
        height: BAR,
        backgroundColor: semantic.interactive.primaryHover,
      },
      // The line's blue, faded by opacity (any colour format works). 18% reads on the white card but
      // all but vanishes on the near-black one.
      glow: {
        position: 'absolute',
        left: 0,
        width: GLOW,
        height: GLOW,
        borderRadius: GLOW / 2,
        backgroundColor: semantic.interactive.primaryHover,
        opacity: mode === 'dark' ? 0.3 : 0.18,
      },
      dot: {
        position: 'absolute',
        left: (GLOW - DOT) / 2,
        width: DOT,
        height: DOT,
        borderRadius: DOT / 2,
        backgroundColor: semantic.interactive.primaryHover,
      },
    }),
  );
  // Between rows the line covers the row's divider; at an edge it moves inside by the glow's radius.
  // Whole points either way, so the hairline stays crisp. The dot and the glow centre on the line.
  const barTop = edge === 'top' ? GLOW / 2 : edge === 'bottom' ? -GLOW / 2 - BAR : -BAR;
  const centre = barTop + BAR / 2;
  return (
    <View
      testID="now-line"
      style={styles.wrap}
      onLayout={onLayout}
      accessibilityElementsHidden
      importantForAccessibility="no-hide-descendants"
      aria-hidden
    >
      <View testID="now-line-bar" style={[styles.bar, { top: barTop }]} />
      <View testID="now-line-glow" style={[styles.glow, { top: centre - GLOW / 2 }]} />
      <View testID="now-line-dot" style={[styles.dot, { top: centre - DOT / 2 }]} />
    </View>
  );
};

NowLine.displayName = 'NowLine';

/**
 * `rows` with the line spliced in above row `index` (null: no line, the same array back) — the two
 * day lists' shared seam. Above the first row it takes the top edge, under the last the bottom one,
 * which moves it inside the row by the glow's radius so a list that clips at its own edges (the home
 * card's) can't cut the glow. Pass `insetEdges: false` for a list that doesn't clip and has room
 * around it (the Planner's): the line then sits ON the boundary at every index, as between rows.
 * Keyed by its index, so a move re-mounts the line and react-native-web — whose onLayout comes only
 * from a ResizeObserver, which never reports a pure move — measures it afresh.
 */
export function insertNowLine(
  rows: React.ReactElement[],
  index: number | null,
  onLayout?: (e: LayoutChangeEvent) => void,
  { insetEdges = true }: { insetEdges?: boolean } = {},
): React.ReactElement[] {
  if (index === null) return rows;
  const edge = !insetEdges ? undefined : index === 0 ? 'top' : index >= rows.length ? 'bottom' : undefined;
  const out = rows.slice();
  out.splice(index, 0, <NowLine key={`now-line-${index}`} edge={edge} onLayout={onLayout} />);
  return out;
}
